import { matchPath } from 'react-router-dom';

import { createSelector } from '@reduxjs/toolkit';
import type { Attachment } from 'attachment/api/types';
import type { RootState } from 'store';

import { matchedSelectors, usersSelectors } from './openNatter.slice';

/**
 * Open Natter Selectors
 */

// Basic state selectors
export const selectOpenNatterPhase = (state: RootState) => state.openNatter.phase;
export const selectOpenNatterState = (state: RootState) => state.openNatter;
export const selectOpenNatterTopic = (state: RootState) => state.openNatter.topic;
export const selectIsReadyToBeMatched = (state: RootState) => state.openNatter.isReadyToBeMatched;
export const selectStartingSoon = (state: RootState) => state.openNatter.startingSoon;
export const selectIsLoading = (state: RootState) => state.openNatter.isLoading;
export const selectIsMarkingReady = (state: RootState) => state.openNatter.isMarkingReady;
export const selectIsSocketConnected = (state: RootState) => state.openNatter.isSocketConnected;
export const selectIsSocketConnecting = (state: RootState) => state.openNatter.isSocketConnecting;

// Video selectors - memoized to prevent unnecessary re-renders
const selectVideoUrls = (state: RootState) => state.openNatter.videos;
export const selectVideos = createSelector([selectVideoUrls], (videos) =>
  videos.map((videoUrl) => ({ url: videoUrl }) as unknown as Attachment)
);

// Waiting room users (entity adapter) - memoized to prevent unnecessary re-renders
const selectWaitingRoomUsersRaw = (state: RootState) => usersSelectors.selectAll(state.openNatter.users);
export const selectWaitingRoomUsers = createSelector([selectWaitingRoomUsersRaw], (users) =>
  users.map((user) => ({
    id: user.id,
    displayName: user.name,
    avatarLink: user.avatarLink,
    ready: user.ready,
  }))
);

// Matched room users (entity adapter) - memoized for consistency
const selectMatchedUsersRaw = (state: RootState) => state.openNatter.matched;
export const selectMatchedUsers = createSelector([selectMatchedUsersRaw], (matched) =>
  matchedSelectors.selectAll(matched)
);

/**
 * Extract Open Natter ID from route
 * This selector reads from the router state to get the current Open Natter ID
 */
export const selectOpenNatterId = (state: RootState) => {
  const pathname = state.router.location?.pathname;
  if (!pathname) return undefined;

  // Match against Open Natter route pattern (/natters/:id/...)
  const match = matchPath<{ id: string }>(pathname, '/natters/:id');

  return match ? match.params.id : undefined;
};
